import { createContext, useContext, useEffect, type ReactNode } from "react";
import { LOCALE_META, localeFromPath, urlForLocale, type Locale } from "./locale";
import { STRINGS, type Strings } from "./strings";
import { CONTENT, type Content } from "./content";

interface LocaleContextValue {
  locale: Locale;
  /** The string table for the current language. */
  t: Strings;
}

/** Must match `ORIGIN` in scripts/seo-pages.mjs and the hreflang tags. */
const ORIGIN = "https://learn2drawkids.com";

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within a LocaleProvider");
  return ctx;
}

/** Shorthand for the common case of only wanting the strings. */
export function useT(): Strings {
  return useLocale().t;
}

/**
 * Reads the locale from the URL path once, at startup. Switching language is a
 * real navigation (see LocaleSwitcher), not a state change: it gives each
 * language a proper URL, resets every screen cleanly, and means nothing has to
 * thread a locale through the history stack.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = localeFromPath(window.location.pathname);

  // One static index.html is served for every language (Firebase rewrites `**`
  // to it), so the language-specific head tags have to be corrected here.
  // Without this, /fr/ would advertise lang="en" and canonicalise itself to the
  // English homepage — telling Google the French site is a duplicate.
  useEffect(() => {
    const meta = LOCALE_META[locale];
    document.documentElement.lang = meta.htmlLang;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `${ORIGIN}${locale === "en" ? "/" : `/${locale}/`}`;

    const ogLocale = document.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (ogLocale) ogLocale.content = meta.htmlLang;

    document.title =
      locale === "fr"
        ? "Apprends 2 Dessiner — Application de Dessin Gratuite pour Enfants, Étape par Étape 🎨"
        : locale === "el"
          ? "Learn 2 Draw — Δωρεάν Εφαρμογή Ζωγραφικής για Μικρά Παιδιά, Βήμα Βήμα 🎨"
          : locale === "nl"
            ? "Learn 2 Draw — Gratis Tekenwebsite & App voor Kleine Kinderen & Peuters, Stap voor Stap 🎨"
            : "Learn 2 Draw — Free Drawing App & Website for Little Kids & Toddlers, Step by Step 🎨";
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, t: STRINGS[locale] }}>
      {children}
    </LocaleContext.Provider>
  );
}

/** Navigate to the same page in another language. */
export function switchLocale(to: Locale): void {
  window.location.assign(urlForLocale(to));
}

/** The current language's card decks for the sub-pages (facts, why, privacy…). */
export function useContent(): Content {
  return CONTENT[useLocale().locale];
}
